import { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { http, API } from "../api";
import {
  FileText, Plus, Copy as CopyIcon, Check, Share2, Trash2, Pencil, Eye, Download,
  GripVertical, X, Users, QrCode, Search
} from "lucide-react";
import { Modal, PageHeader, EmptyState, FormRow } from "../shared";

const FIELD_TYPES = [
  { id: "short_text", label: "Short text" },
  { id: "long_text", label: "Long text" },
  { id: "number", label: "Number" },
  { id: "single_select", label: "Dropdown (single-select)" },
  { id: "multi_select", label: "Checkboxes (multi-select)" },
  { id: "radio", label: "Radio buttons" },
  { id: "date", label: "Date" },
  { id: "phone", label: "Phone" },
  { id: "email", label: "Email" },
  { id: "rating", label: "Star rating (1-5)" },
  { id: "boolean", label: "Yes / No toggle" },
  { id: "file", label: "Single file" },
  { id: "multi_file", label: "Multiple files" },
];
const OPTION_TYPES = new Set(["single_select", "multi_select", "radio"]);
const TEXT_TYPES = new Set(["short_text", "long_text"]);
const NUMBER_TYPES = new Set(["number", "rating"]);

const ACCESS_LEVELS = [
  { id: "super_admin", label: "Super Admin only" },
  { id: "admin", label: "Admin users" },
  { id: "member", label: "Members" },
  { id: "public", label: "Public users" },
  { id: "specific", label: "Specific users" },
];

const emptyForm = {
  title: "", description: "", access_level: "admin",
  specific_user_ids: [], require_login: true, permission: "submit",
  fields: [], is_open: true,
  thank_you_message: "", redirect_url: "", response_limit: 0,
  page_titles: [],
};

export default function Forms({ user }) {
  const isAdmin = user && (user.role === "Super Admin" || user.role === "Admin");
  const isSuper = user && user.role === "Super Admin";
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [subFor, setSubFor] = useState(null);
  const [subs, setSubs] = useState([]);
  const [subSearch, setSubSearch] = useState("");
  const [copiedFor, setCopiedFor] = useState("");
  const [delegateFor, setDelegateFor] = useState(null);
  const [qrFor, setQrFor] = useState(null);

  const load = () => http.get("/forms").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (isAdmin) http.get("/users").then((r) => setUsers(r.data)).catch(() => { }); }, [isAdmin]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setSaveErr(""); setShow(true); };
  const openEdit = (f) => { setEditing(f); setForm({ ...emptyForm, ...f }); setSaveErr(""); setShow(true); };

  const addField = () => {
    setForm((f) => ({
      ...f,
      fields: [...f.fields, {
        field_id: `fld_${Math.random().toString(36).slice(2, 8)}`,
        label: "New field", type: "short_text", required: false,
        placeholder: "", help_text: "", options: [], order: f.fields.length,
        page: 1, visible_if: null,
      }],
    }));
  };
  const updateField = (i, patch) => setForm((f) => ({
    ...f, fields: f.fields.map((x, idx) => idx === i ? { ...x, ...patch } : x),
  }));
  const removeField = (i) => setForm((f) => ({ ...f, fields: f.fields.filter((_, idx) => idx !== i) }));
  const moveField = (i, dir) => setForm((f) => {
    const j = i + dir;
    if (j < 0 || j >= f.fields.length) return f;
    const c = [...f.fields]; [c[i], c[j]] = [c[j], c[i]];
    return { ...f, fields: c };
  });

  const save = async () => {
    setSaving(true); setSaveErr("");
    try {
      // Clean options text (one per line) into arrays
      const payload = {
        ...form,
        response_limit: Number(form.response_limit) || 0,
        fields: form.fields.map((f) => ({
          ...f,
          options: OPTION_TYPES.has(f.type) ? (Array.isArray(f.options) ? f.options : String(f.options || "").split("\n").map((s) => s.trim()).filter(Boolean)) : [],
        })),
      };
      if (editing) await http.patch(`/forms/${editing.form_id}`, payload);
      else await http.post("/forms", payload);
      setShow(false); setEditing(null); setForm(emptyForm); load();
    } catch (e) {
      setSaveErr(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete "${f.title}" and all its submissions?`)) return;
    await http.delete(`/forms/${f.form_id}`); load();
  };

  const duplicate = async (f) => {
    await http.post(`/forms/${f.form_id}/duplicate`);
    load();
  };

  const openSubs = async (f) => {
    setSubFor(f); setSubSearch(""); setSubs([]);
    const r = await http.get(`/forms/${f.form_id}/submissions`);
    setSubs(r.data.submissions);
  };

  const shareUrlFor = (f) => `${window.location.origin}/f/${f.share_slug}`;
  const copyShare = async (f) => {
    try { await navigator.clipboard.writeText(shareUrlFor(f)); } catch { /* noop */ }
    setCopiedFor(f.form_id);
    setTimeout(() => setCopiedFor(""), 2000);
  };

  const downloadCsv = (f) => window.open(`${API}/forms/${f.form_id}/submissions.csv`, "_blank");

  const openDelegate = async (f) => setDelegateFor({ ...f, delegated_to: f.delegated_to || [] });
  const saveDelegate = async () => {
    try {
      await http.post(`/forms/${delegateFor.form_id}/delegate`, { user_ids: delegateFor.delegated_to });
      setDelegateFor(null); load();
    } catch (e) {
      alert(e.response?.data?.detail || "Delegation failed");
    }
  };

  const filteredSubs = useMemo(() => {
    if (!subSearch.trim()) return subs;
    const q = subSearch.toLowerCase();
    return subs.filter((s) => {
      const hay = [
        s.submitted_by_name, s.submitter_email,
        ...Object.values(s.values || {}).map((v) => Array.isArray(v) ? v.join(" ") : String(v || "")),
      ].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [subs, subSearch]);

  return (
    <>
      <PageHeader eyebrow="ENGAGEMENT" title="Custom forms" subtitle={`${rows.length} form${rows.length === 1 ? "" : "s"} available to you`}
        actions={isAdmin ? <button className="primary" data-testid="forms-add-button" onClick={openNew}><Plus size={16} /> New form</button> : null} />

      <section className="panel">
        {rows.length === 0 ? <EmptyState icon={FileText} title="No forms yet" hint="Create a form to gather submissions." /> : (
          <div className="grid-cards">
            {rows.map((f, i) => (
              <div key={f.form_id} className="org-card" data-testid={`form-card-${i}`}>
                <div className="org-head">
                  <FileText size={18} /><b>{f.title}</b>
                  <span className={`chip ${f.is_open ? "green" : "amber"}`} data-testid={`form-status-${i}`}>{f.is_open ? "Open" : "Closed"}</span>
                  <span className="chip blue">{ACCESS_LEVELS.find((a) => a.id === f.access_level)?.label || f.access_level}</span>
                </div>
                <p className="hint">{f.description}</p>
                <p className="hint" data-testid={`form-meta-${i}`}>
                  {(f.fields || []).length} field{(f.fields || []).length === 1 ? "" : "s"} · <b data-testid={`form-sub-count-${i}`}>{f.submission_count || 0}</b> submission{(f.submission_count || 0) === 1 ? "" : "s"}
                  {f.response_limit ? ` · cap ${f.response_limit}` : ""}
                </p>
                <div className="row-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
                  <button className="link" data-testid={`form-share-${i}`} onClick={() => copyShare(f)}>
                    {copiedFor === f.form_id ? <><Check size={12} /> Copied</> : <><Share2 size={12} /> Copy link</>}
                  </button>
                  <button className="link" data-testid={`form-qr-${i}`} onClick={() => setQrFor(f)}><QrCode size={12} /> QR code</button>
                  <button className="link" data-testid={`form-subs-${i}`} onClick={() => openSubs(f)}>
                    <Users size={12} /> Submissions
                  </button>
                  {isAdmin && (
                    <>
                      <button className="link" data-testid={`form-edit-${i}`} onClick={() => openEdit(f)}><Pencil size={12} /> Edit</button>
                      <button className="link" data-testid={`form-duplicate-${i}`} onClick={() => duplicate(f)}><CopyIcon size={12} /> Duplicate</button>
                      {isSuper && <button className="link" data-testid={`form-delegate-${i}`} onClick={() => openDelegate(f)}><Eye size={12} /> Delegate</button>}
                      <button className="link danger" data-testid={`form-delete-${i}`} onClick={() => del(f)}><Trash2 size={12} /> Delete</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {show && (
        <Modal title={editing ? `Edit form` : "New form"} onClose={() => { setShow(false); setEditing(null); }}
          testid="form-builder-modal"
          isDirty={() => JSON.stringify(form) !== JSON.stringify(editing ? { ...emptyForm, ...editing } : emptyForm)}>
          <div className="form-grid">
            <FormRow label="Title"><input data-testid="fb-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></FormRow>
            <FormRow label="Access level">
              <select data-testid="fb-access" value={form.access_level} onChange={(e) => setForm({ ...form, access_level: e.target.value })}>
                {ACCESS_LEVELS.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
            </FormRow>
            <FormRow label="Permission">
              <select data-testid="fb-perm" value={form.permission} onChange={(e) => setForm({ ...form, permission: e.target.value })}>
                <option value="view">View only</option><option value="submit">Submit only</option><option value="both">View + submit</option>
              </select>
            </FormRow>
            <FormRow label="Require login for public link">
              <select data-testid="fb-login" value={form.require_login ? "yes" : "no"} onChange={(e) => setForm({ ...form, require_login: e.target.value === "yes" })}>
                <option value="yes">Yes</option><option value="no">No — anyone with link</option>
              </select>
            </FormRow>
            <FormRow label="Response limit (0 = unlimited)">
              <input type="number" min="0" data-testid="fb-response-limit" value={form.response_limit || 0} onChange={(e) => setForm({ ...form, response_limit: Number(e.target.value) || 0 })} />
            </FormRow>
            <FormRow label="Form status">
              <select data-testid="fb-open" value={form.is_open ? "open" : "closed"} onChange={(e) => setForm({ ...form, is_open: e.target.value === "open" })}>
                <option value="open">Open — accepting responses</option>
                <option value="closed">Closed</option>
              </select>
            </FormRow>
          </div>
          <FormRow label="Description"><textarea data-testid="fb-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormRow>
          <FormRow label="Thank-you message (shown after submit)">
            <textarea data-testid="fb-thanks" rows={2} placeholder="Thanks for reaching out! We'll get back within 3 working days."
              value={form.thank_you_message} onChange={(e) => setForm({ ...form, thank_you_message: e.target.value })} />
          </FormRow>
          <FormRow label="Redirect URL (optional, replaces thank-you page)">
            <input data-testid="fb-redirect" placeholder="https://…" value={form.redirect_url} onChange={(e) => setForm({ ...form, redirect_url: e.target.value })} />
          </FormRow>

          {(() => {
            const totalPagesInForm = Math.max(1, ...form.fields.map((f) => f.page || 1));
            if (totalPagesInForm <= 1) return null;
            const titles = form.page_titles || [];
            return (
              <FormRow label={`Step titles (${totalPagesInForm} pages detected)`}>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }} data-testid="fb-page-titles">
                  {Array.from({ length: totalPagesInForm }, (_, i) => (
                    <input key={i} data-testid={`fb-page-title-${i + 1}`}
                      placeholder={`Step ${i + 1} title (optional) — e.g. "About you"`}
                      value={titles[i] || ""}
                      onChange={(e) => {
                        const next = [...titles];
                        next[i] = e.target.value;
                        setForm({ ...form, page_titles: next });
                      }} />
                  ))}
                </div>
              </FormRow>
            );
          })()}

          {form.access_level === "specific" && (
            <FormRow label="Specific users allowed">
              <select multiple data-testid="fb-specific" value={form.specific_user_ids} onChange={(e) => setForm({ ...form, specific_user_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })} style={{ minHeight: 100 }}>
                {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} — {u.email}</option>)}
              </select>
            </FormRow>
          )}

          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <p className="eyebrow" style={{ margin: 0 }}>FORM FIELDS ({form.fields.length})</p>
              <button type="button" className="outline" onClick={addField} data-testid="fb-add-field"><Plus size={12} /> Add field</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }} data-testid="fb-field-list">
              {form.fields.map((fl, i) => (
                <FieldEditor key={fl.field_id} field={fl} idx={i}
                  fieldsBefore={form.fields.slice(0, i)}
                  totalPages={Math.max(1, ...form.fields.map((x) => x.page || 1))}
                  onChange={(patch) => updateField(i, patch)}
                  onRemove={() => removeField(i)}
                  onMove={(dir) => moveField(i, dir)} />
              ))}
              {form.fields.length === 0 && <p className="hint">No fields yet — click &ldquo;Add field&rdquo; to start.</p>}
            </div>
          </div>

          <div className="form-actions">
            {saveErr && <p className="hint" data-testid="fb-error" style={{ color: "#b91c1c", flexBasis: "100%", marginBottom: 6 }}>{saveErr}</p>}
            <button className="outline" onClick={() => { setShow(false); setEditing(null); }}>Cancel</button>
            <button className="primary" data-testid="fb-save" disabled={saving || !form.title} onClick={save}>{saving ? "Saving…" : editing ? "Save changes" : "Create form"}</button>
          </div>
        </Modal>
      )}

      {subFor && (
        <Modal title={`${subFor.title} · Submissions`} onClose={() => setSubFor(null)} testid="submissions-modal">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
              <Search size={14} style={{ opacity: .5 }} />
              <input data-testid="submissions-search" placeholder="Search submissions…" value={subSearch} onChange={(e) => setSubSearch(e.target.value)} style={{ width: "100%", padding: 6, border: "1px solid var(--line)" }} />
            </div>
            <p className="hint" style={{ margin: 0, whiteSpace: "nowrap" }} data-testid="submissions-count">{filteredSubs.length} of {subs.length}</p>
            <button className="outline" data-testid="submissions-csv" onClick={() => downloadCsv(subFor)}><Download size={14} /> Export CSV</button>
          </div>
          {filteredSubs.length === 0 ? <EmptyState icon={FileText} title={subs.length === 0 ? "No submissions yet" : "No matches"} hint={subs.length === 0 ? "Share the form link to gather responses." : "Try a different search term."} /> : (
            <div className="finance-table" data-testid="submissions-list" style={{ maxHeight: 420, overflowY: "auto" }}>
              <div className="tr th"><span>When</span><span>By</span><span>Email</span><span>Values</span></div>
              {filteredSubs.map((s, i) => (
                <div key={s.submission_id} className="tr" data-testid={`sub-row-${i}`}>
                  <span><small>{new Date(s.created_at).toLocaleString()}</small></span>
                  <span>{s.submitted_by_name}</span>
                  <span><small>{s.submitter_email || "—"}</small></span>
                  <span><small>{Object.entries(s.values || {}).map(([k, v]) => {
                    const lbl = (subFor.fields || []).find((f) => f.field_id === k)?.label || k;
                    let val = v;
                    if (Array.isArray(v)) val = v.every((x) => typeof x === "string" && x.startsWith("file_")) ? `${v.length} file(s)` : v.join(", ");
                    else if (typeof v === "boolean") val = v ? "Yes" : "No";
                    return `${lbl}: ${val}`;
                  }).join(" · ")}</small></span>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}

      {qrFor && (
        <Modal title={`Share ${qrFor.title}`} onClose={() => setQrFor(null)} testid="qr-modal">
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, padding: "10px 0" }}>
            <div style={{ background: "white", padding: 16, border: "1px solid var(--line)" }} data-testid="qr-svg">
              <QRCodeSVG value={shareUrlFor(qrFor)} size={220} bgColor="#ffffff" fgColor="#0f766e" level="M" includeMargin={false} />
            </div>
            <p className="hint" style={{ margin: 0, wordBreak: "break-all", textAlign: "center", maxWidth: 320 }}>{shareUrlFor(qrFor)}</p>
            <div style={{ display: "flex", gap: 8 }}>
              <button className="outline" onClick={() => copyShare(qrFor)} data-testid="qr-copy">
                {copiedFor === qrFor.form_id ? <><Check size={13} /> Copied</> : <><CopyIcon size={13} /> Copy link</>}
              </button>
              <button className="outline" onClick={() => downloadQr(qrFor)} data-testid="qr-download"><Download size={13} /> Download PNG</button>
            </div>
          </div>
        </Modal>
      )}

      {delegateFor && (
        <Modal title={`Delegate submissions access — ${delegateFor.title}`} onClose={() => setDelegateFor(null)} testid="delegate-modal">
          <p className="hint">Selected admins will see submissions of this form and can export them.</p>
          <select multiple size={8} data-testid="delegate-select" value={delegateFor.delegated_to} onChange={(e) => setDelegateFor({ ...delegateFor, delegated_to: Array.from(e.target.selectedOptions).map((o) => o.value) })} style={{ width: "100%", minHeight: 200 }}>
            {users.filter((u) => u.role === "Admin" || u.role === "Super Admin").map((u) => <option key={u.user_id} value={u.user_id}>{u.name} — {u.role}</option>)}
          </select>
          <div className="form-actions">
            <button className="outline" onClick={() => setDelegateFor(null)}>Cancel</button>
            <button className="primary" data-testid="delegate-save" onClick={saveDelegate}>Save delegation</button>
          </div>
        </Modal>
      )}
    </>
  );
}

function FieldEditor({ field, idx, fieldsBefore = [], totalPages = 1, onChange, onRemove, onMove }) {
  const showOptions = OPTION_TYPES.has(field.type);
  const showText = TEXT_TYPES.has(field.type);
  const showNum = NUMBER_TYPES.has(field.type);
  const optionsText = Array.isArray(field.options) ? field.options.join("\n") : String(field.options || "");
  // Only fields with a knowable set of answers make good conditional triggers.
  const CAN_TRIGGER = new Set(["single_select", "radio", "boolean", "short_text"]);
  const triggerCandidates = fieldsBefore.filter((f) => CAN_TRIGGER.has(f.type) && f.label && f.field_id !== field.field_id);
  const cond = field.visible_if || null;
  const triggerField = cond ? fieldsBefore.find((f) => f.field_id === cond.field_id) : null;
  const setCond = (patch) => {
    if (patch === null) { onChange({ visible_if: null }); return; }
    onChange({ visible_if: { ...(cond || { field_id: "", value: "" }), ...patch } });
  };
  const pageOptions = Array.from({ length: Math.max(totalPages, field.page || 1, 1) + 1 }, (_, k) => k + 1);
  return (
    <div data-testid={`fb-field-${idx}`} style={{ background: "#fff", border: "1px solid var(--line)", borderRadius: 8, padding: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "24px 1fr 170px 80px 60px 30px 30px 30px", gap: 8, alignItems: "center" }}>
        <GripVertical size={16} style={{ opacity: 0.4 }} />
        <input placeholder="Field label" value={field.label} onChange={(e) => onChange({ label: e.target.value })} data-testid={`fb-field-label-${idx}`} />
        <select value={field.type} onChange={(e) => onChange({ type: e.target.value, options: OPTION_TYPES.has(e.target.value) ? (field.options || []) : [] })} data-testid={`fb-field-type-${idx}`}>
          {FIELD_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <label style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 4 }} data-testid={`fb-field-required-${idx}`}>
          <input type="checkbox" checked={!!field.required} onChange={(e) => onChange({ required: e.target.checked })} /> Required
        </label>
        <select value={field.page || 1} onChange={(e) => onChange({ page: Number(e.target.value) || 1 })} data-testid={`fb-field-page-${idx}`} title="Page number">
          {pageOptions.map((p) => <option key={p} value={p}>P{p}</option>)}
        </select>
        <button type="button" className="icon-btn" data-testid={`fb-field-up-${idx}`} onClick={() => onMove(-1)}>↑</button>
        <button type="button" className="icon-btn" data-testid={`fb-field-down-${idx}`} onClick={() => onMove(1)}>↓</button>
        <button type="button" className="icon-btn" data-testid={`fb-field-remove-${idx}`} onClick={onRemove}><X size={13} /></button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
        <input placeholder="Placeholder / hint" value={field.placeholder || ""} onChange={(e) => onChange({ placeholder: e.target.value })} data-testid={`fb-field-placeholder-${idx}`} />
        <input placeholder="Help text (shown below the field)" value={field.help_text || ""} onChange={(e) => onChange({ help_text: e.target.value })} data-testid={`fb-field-help-${idx}`} />
      </div>
      {showText && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
          <input type="number" placeholder="Min length" value={field.min_length ?? ""} onChange={(e) => onChange({ min_length: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`fb-field-min-${idx}`} />
          <input type="number" placeholder="Max length" value={field.max_length ?? ""} onChange={(e) => onChange({ max_length: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`fb-field-max-${idx}`} />
        </div>
      )}
      {showNum && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 6 }}>
          <input type="number" placeholder="Min value" value={field.min_value ?? ""} onChange={(e) => onChange({ min_value: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`fb-field-minv-${idx}`} />
          <input type="number" placeholder="Max value" value={field.max_value ?? ""} onChange={(e) => onChange({ max_value: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`fb-field-maxv-${idx}`} />
        </div>
      )}
      {showOptions && (
        <div style={{ marginTop: 6 }}>
          <textarea rows={3} placeholder="Options — one per line" value={optionsText}
            onChange={(e) => onChange({ options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
            data-testid={`fb-field-options-${idx}`}
            style={{ width: "100%" }} />
          <small className="hint">One option per line. Order is preserved.</small>
        </div>
      )}
      {/* Conditional visibility */}
      <div style={{ marginTop: 8, padding: 8, background: "#f6f8f5", borderRadius: 6, borderLeft: "3px solid #a18552" }} data-testid={`fb-field-cond-${idx}`}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#55716a" }}>
          <b>SHOW ONLY WHEN</b>
          <select value={cond?.field_id || ""} onChange={(e) => setCond(e.target.value ? { field_id: e.target.value, value: "" } : null)} data-testid={`fb-field-cond-src-${idx}`} style={{ flex: 1 }}>
            <option value="">— always show —</option>
            {triggerCandidates.map((c) => <option key={c.field_id} value={c.field_id}>{c.label}</option>)}
          </select>
          <span>EQUALS</span>
          {triggerField && (triggerField.type === "single_select" || triggerField.type === "radio") ? (
            <select value={cond?.value || ""} onChange={(e) => setCond({ value: e.target.value })} data-testid={`fb-field-cond-val-${idx}`} style={{ flex: 1 }}>
              <option value="">— pick a value —</option>
              {(triggerField.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : triggerField && triggerField.type === "boolean" ? (
            <select value={cond?.value || ""} onChange={(e) => setCond({ value: e.target.value })} data-testid={`fb-field-cond-val-${idx}`} style={{ flex: 1 }}>
              <option value="">— pick a value —</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          ) : (
            <input value={cond?.value || ""} onChange={(e) => setCond({ value: e.target.value })} disabled={!cond?.field_id} placeholder={cond?.field_id ? "value…" : "select field first"} data-testid={`fb-field-cond-val-${idx}`} style={{ flex: 1 }} />
          )}
        </div>
        {!triggerCandidates.length && <small className="hint" style={{ marginTop: 4, display: "block" }}>Add a dropdown / radio / yes-no / short-text field above this one to enable conditional visibility.</small>}
      </div>
    </div>
  );
}

function downloadQr(f) {
  // Render an SVG string and convert to PNG via canvas
  const url = `${window.location.origin}/f/${f.share_slug}`;
  const svg = document.querySelector('[data-testid="qr-svg"] svg');
  if (!svg) return;
  const xml = new XMLSerializer().serializeToString(svg);
  const svg64 = btoa(unescape(encodeURIComponent(xml)));
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = 512; canvas.height = 512;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, 512, 512);
    ctx.drawImage(img, 0, 0, 512, 512);
    const link = document.createElement("a");
    link.download = `${f.title.replace(/\s+/g, "-").toLowerCase() || "form"}-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };
  img.src = `data:image/svg+xml;base64,${svg64}`;
  void url;
}
